import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart3, Download, Search, Filter,
  Package, ShoppingCart, ArrowUpDown, ClipboardList, FileSpreadsheet
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { reportsApi, vendorsApi, clientsApi, itemsApi } from '../api/api';

/* ── Excel Export Utility ────────────────────────────────────── */
function exportToExcel(data, columns, filename, sheetName = 'Report') {
  if (!data || !data.length) return;
  const ws = XLSX.utils.json_to_sheet(
    data.map(row => {
      const out = {};
      columns.forEach(col => { out[col.header] = col.value(row); });
      return out;
    })
  );
  // Auto-width
  const colWidths = columns.map(col => ({
    wch: Math.max(col.header.length, ...data.map(r => String(col.value(r) ?? '').length)) + 2
  }));
  ws['!cols'] = colWidths;
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`);
}

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN') : '—';
const fmtCur  = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });

/* ── Reusable Filter Bar ─────────────────────────────────────── */
function FilterBar({ children, onExport, exportLabel = 'Export Excel', exporting }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '12px 18px', borderBottom: '1px solid var(--border)' }}>
      <Filter size={13} style={{ color: 'var(--gray-400)' }} />
      {children}
      <div style={{ flex: 1 }} />
      <button className="btn" onClick={onExport} disabled={exporting}
        style={{ background: '#217346', color: '#fff', border: 'none', gap: 6 }}>
        <FileSpreadsheet size={14} /> {exportLabel}
      </button>
    </div>
  );
}

/* ── TAB 1: Stock Report ─────────────────────────────────────── */
function StockReport() {
  const [category, setCategory] = useState('');
  const [status,   setStatus]   = useState('active');
  const [lowStock, setLowStock] = useState(false);
  const [search,   setSearch]   = useState('');

  const { data: categories = [] } = useQuery({ queryKey: ['categories'], queryFn: () => itemsApi.getCategories() });
  const { data: items = [], isLoading } = useQuery({
    queryKey: ['report-stock', category, status, lowStock],
    queryFn: () => reportsApi.stock({ category, status, low_stock: lowStock }),
  });

  const filtered = items.filter(i =>
    !search || i.name.toLowerCase().includes(search.toLowerCase()) || i.item_code.toLowerCase().includes(search.toLowerCase())
  );

  const totalValue = filtered.reduce((s, i) => s + Number(i.stock_value || 0), 0);

  const COLUMNS = [
    { header: 'Item Code',      value: r => r.item_code },
    { header: 'Item Name',      value: r => r.name },
    { header: 'Category',       value: r => r.category },
    { header: 'Unit',           value: r => r.unit },
    { header: 'Stock Qty',      value: r => r.stock_qty },
    { header: 'Reorder Qty',    value: r => r.reorder_qty },
    { header: 'Low Stock',      value: r => r.stock_qty <= r.reorder_qty ? 'YES' : 'NO' },
    { header: 'Unit Price',     value: r => Number(r.unit_price) },
    { header: 'Stock Value',    value: r => Number(r.stock_value || 0) },
    { header: 'Total Purchased',value: r => r.total_purchased },
    { header: 'Total Issued',   value: r => r.total_issued },
    { header: 'Total Received (MRN)', value: r => r.total_received_mrn },
    { header: 'HSN Code',       value: r => r.hsn_code },
    { header: 'Status',         value: r => r.status },
  ];

  return (
    <div>
      <FilterBar onExport={() => exportToExcel(filtered, COLUMNS, 'Stock_Report', 'Stock')}>
        <div className="search-wrap" style={{ background: '#fff' }}>
          <Search size={13} />
          <input placeholder="Search items…" value={search} onChange={e => setSearch(e.target.value)} style={{ width: 160 }} />
        </div>
        <select className="filter-select" value={category} onChange={e => setCategory(e.target.value)}>
          <option value="">All Categories</option>
          {categories.map(c => <option key={c}>{c}</option>)}
        </select>
        <select className="filter-select" value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', color: 'var(--gray-600)' }}>
          <input type="checkbox" checked={lowStock} onChange={e => setLowStock(e.target.checked)} />
          Low Stock Only
        </label>
      </FilterBar>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, padding: 16 }}>
        <div style={{ padding: 14, background: 'var(--accent-light)', borderRadius: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent-text)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Total Items</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--accent)', fontFamily: 'var(--mono)' }}>{filtered.length}</div>
        </div>
        <div style={{ padding: 14, background: 'var(--blue-light)', borderRadius: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--blue)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Total Stock Value</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--blue)', fontFamily: 'var(--mono)' }}>₹{totalValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
        </div>
        <div style={{ padding: 14, background: 'var(--red-light)', borderRadius: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--red)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Low Stock Items</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--red)', fontFamily: 'var(--mono)' }}>{filtered.filter(i => i.stock_qty <= i.reorder_qty).length}</div>
        </div>
      </div>

      <div className="table-wrap">
        {isLoading ? <div className="spinner" /> : (
          <table>
            <thead><tr>
              <th>Code</th><th>Item Name</th><th>Category</th><th>Unit</th>
              <th>Stock Qty</th><th>Reorder</th><th>Unit Price</th><th>Stock Value</th><th>Purchased</th><th>Issued</th>
            </tr></thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={10}><div className="empty-state"><Package /><p>No items found</p></div></td></tr>}
              {filtered.map(item => {
                const isLow = item.stock_qty <= item.reorder_qty;
                return (
                  <tr key={item.id}>
                    <td className="mono">{item.item_code}</td>
                    <td style={{ fontWeight: 500 }}>
                      {isLow && <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: 'var(--red)', marginRight: 6 }} />}
                      {item.name}
                    </td>
                    <td>{item.category || '—'}</td>
                    <td>{item.unit}</td>
                    <td style={{ fontWeight: 600, color: isLow ? 'var(--red)' : 'var(--accent)' }}>{item.stock_qty}</td>
                    <td>{item.reorder_qty}</td>
                    <td className="amount">₹{fmtCur(item.unit_price)}</td>
                    <td className="amount" style={{ fontWeight: 600 }}>₹{fmtCur(item.stock_value)}</td>
                    <td>{item.total_purchased}</td>
                    <td>{item.total_issued}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={7} style={{ padding: '10px 18px', textAlign: 'right', fontWeight: 600, background: 'var(--gray-50)' }}>Total Stock Value</td>
                <td colSpan={3} style={{ padding: '10px 18px', fontWeight: 700, fontFamily: 'var(--mono)', background: 'var(--gray-50)' }}>₹{totalValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </div>
  );
}

/* ── TAB 2: Purchase Report ──────────────────────────────────── */
function PurchaseReport() {
  const [fromDate, setFrom] = useState('');
  const [toDate,   setTo]   = useState('');
  const [vendorId, setVendor] = useState('');
  const [status,   setStatus] = useState('');

  const { data: vendors = [] } = useQuery({ queryKey: ['vendors'], queryFn: () => import('../api/api').then(m => m.vendorsApi.getAll({})) });
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['report-purchases', fromDate, toDate, vendorId, status],
    queryFn: () => reportsApi.purchases({ from_date: fromDate, to_date: toDate, vendor_id: vendorId, status }),
  });

  const total = rows.reduce((s, r) => s + Number(r.total_amount || 0), 0);
  const COLUMNS = [
    { header: 'PO Number',    value: r => r.po_number },
    { header: 'Vendor',       value: r => r.vendor_name },
    { header: 'Vendor Code',  value: r => r.vendor_code },
    { header: 'City',         value: r => r.vendor_city },
    { header: 'Order Date',   value: r => fmtDate(r.order_date) },
    { header: 'Expected Date',value: r => fmtDate(r.expected_date) },
    { header: 'Status',       value: r => r.status },
    { header: 'Items Count',  value: r => r.item_count },
    { header: 'MRN Count',    value: r => r.mrn_count },
    { header: 'Total Amount', value: r => Number(r.total_amount) },
    { header: 'Notes',        value: r => r.notes },
  ];

  return (
    <div>
      <FilterBar onExport={() => exportToExcel(rows, COLUMNS, 'Purchase_Report', 'Purchases')}>
        <input className="filter-select" type="date" value={fromDate} onChange={e => setFrom(e.target.value)} title="From Date" />
        <input className="filter-select" type="date" value={toDate} onChange={e => setTo(e.target.value)} title="To Date" />
        <select className="filter-select" value={vendorId} onChange={e => setVendor(e.target.value)}>
          <option value="">All Vendors</option>
          {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        <select className="filter-select" value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="received">Received</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </FilterBar>
      <div className="table-wrap">
        {isLoading ? <div className="spinner" /> : (
          <table>
            <thead><tr>
              <th>PO Number</th><th>Vendor</th><th>Order Date</th><th>Expected</th><th>Items</th><th>MRNs</th><th>Amount</th><th>Status</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={8}><div className="empty-state"><ShoppingCart /><p>No purchase orders found</p></div></td></tr>}
              {rows.map(r => (
                <tr key={r.id}>
                  <td className="mono">{r.po_number}</td>
                  <td style={{ fontWeight: 500 }}>{r.vendor_name}<div style={{ fontSize: 11, color: 'var(--gray-400)' }}>{r.vendor_code}</div></td>
                  <td>{fmtDate(r.order_date)}</td>
                  <td>{fmtDate(r.expected_date)}</td>
                  <td style={{ textAlign: 'center' }}>{r.item_count}</td>
                  <td style={{ textAlign: 'center' }}>{r.mrn_count}</td>
                  <td className="amount">₹{fmtCur(r.total_amount)}</td>
                  <td><span className={`badge badge-${r.status}`}>{r.status}</span></td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr>
              <td colSpan={6} style={{ padding: '10px 18px', textAlign: 'right', fontWeight: 600, background: 'var(--gray-50)' }}>Total</td>
              <td colSpan={2} style={{ padding: '10px 18px', fontWeight: 700, fontFamily: 'var(--mono)', background: 'var(--gray-50)' }}>₹{total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
            </tr></tfoot>
          </table>
        )}
      </div>
    </div>
  );
}

/* ── TAB 3: Issues Report ────────────────────────────────────── */
function IssueReport() {
  const [fromDate, setFrom]   = useState('');
  const [toDate,   setTo]     = useState('');
  const [clientId, setClient] = useState('');
  const [status,   setStatus] = useState('');

  const { data: clients = [] } = useQuery({ queryKey: ['clients'], queryFn: () => import('../api/api').then(m => m.clientsApi.getAll({})) });
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['report-issues', fromDate, toDate, clientId, status],
    queryFn: () => reportsApi.issues({ from_date: fromDate, to_date: toDate, client_id: clientId, status }),
  });

  const total = rows.reduce((s, r) => s + Number(r.total_amount || 0), 0);
  const COLUMNS = [
    { header: 'Issue Number', value: r => r.issue_number },
    { header: 'Client',       value: r => r.client_name },
    { header: 'Client Code',  value: r => r.client_code },
    { header: 'City',         value: r => r.client_city },
    { header: 'Issue Date',   value: r => fmtDate(r.issue_date) },
    { header: 'Status',       value: r => r.status },
    { header: 'Items Count',  value: r => r.item_count },
    { header: 'Total Amount', value: r => Number(r.total_amount) },
    { header: 'Notes',        value: r => r.notes },
  ];

  return (
    <div>
      <FilterBar onExport={() => exportToExcel(rows, COLUMNS, 'Issue_Report', 'Issues')}>
        <input className="filter-select" type="date" value={fromDate} onChange={e => setFrom(e.target.value)} />
        <input className="filter-select" type="date" value={toDate}   onChange={e => setTo(e.target.value)} />
        <select className="filter-select" value={clientId} onChange={e => setClient(e.target.value)}>
          <option value="">All Clients</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select className="filter-select" value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">All Status</option>
          <option value="draft">Draft</option>
          <option value="issued">Issued</option>
          <option value="returned">Returned</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </FilterBar>
      <div className="table-wrap">
        {isLoading ? <div className="spinner" /> : (
          <table>
            <thead><tr>
              <th>Issue No.</th><th>Client</th><th>Issue Date</th><th>Items</th><th>Amount</th><th>Status</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={6}><div className="empty-state"><ArrowUpDown /><p>No issues found</p></div></td></tr>}
              {rows.map(r => (
                <tr key={r.id}>
                  <td className="mono">{r.issue_number}</td>
                  <td style={{ fontWeight: 500 }}>{r.client_name}<div style={{ fontSize: 11, color: 'var(--gray-400)' }}>{r.client_code}</div></td>
                  <td>{fmtDate(r.issue_date)}</td>
                  <td style={{ textAlign: 'center' }}>{r.item_count}</td>
                  <td className="amount">₹{fmtCur(r.total_amount)}</td>
                  <td><span className={`badge badge-${r.status}`}>{r.status}</span></td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr>
              <td colSpan={4} style={{ padding: '10px 18px', textAlign: 'right', fontWeight: 600, background: 'var(--gray-50)' }}>Total</td>
              <td colSpan={2} style={{ padding: '10px 18px', fontWeight: 700, fontFamily: 'var(--mono)', background: 'var(--gray-50)' }}>₹{total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
            </tr></tfoot>
          </table>
        )}
      </div>
    </div>
  );
}

/* ── TAB 4: MRN Report ───────────────────────────────────────── */
function MRNReport() {
  const [fromDate, setFrom]   = useState('');
  const [toDate,   setTo]     = useState('');
  const [vendorId, setVendor] = useState('');
  const [status,   setStatus] = useState('');

  const { data: vendors = [] } = useQuery({ queryKey: ['vendors'], queryFn: () => import('../api/api').then(m => m.vendorsApi.getAll({})) });
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['report-mrn', fromDate, toDate, vendorId, status],
    queryFn: () => reportsApi.mrn({ from_date: fromDate, to_date: toDate, vendor_id: vendorId, status }),
  });

  const total = rows.reduce((s, r) => s + Number(r.total_amount || 0), 0);
  const COLUMNS = [
    { header: 'MRN Number',   value: r => r.mrn_number },
    { header: 'PO Number',    value: r => r.po_number },
    { header: 'Vendor',       value: r => r.vendor_name },
    { header: 'Vendor Code',  value: r => r.vendor_code },
    { header: 'Received Date',value: r => fmtDate(r.received_date) },
    { header: 'Received By',  value: r => r.received_by },
    { header: 'Status',       value: r => r.status },
    { header: 'Total Amount', value: r => Number(r.total_amount) },
    { header: 'Remarks',      value: r => r.remarks },
  ];

  return (
    <div>
      <FilterBar onExport={() => exportToExcel(rows, COLUMNS, 'MRN_Report', 'MRN')}>
        <input className="filter-select" type="date" value={fromDate} onChange={e => setFrom(e.target.value)} />
        <input className="filter-select" type="date" value={toDate}   onChange={e => setTo(e.target.value)} />
        <select className="filter-select" value={vendorId} onChange={e => setVendor(e.target.value)}>
          <option value="">All Vendors</option>
          {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        <select className="filter-select" value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">All Status</option>
          <option value="draft">Draft</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </FilterBar>
      <div className="table-wrap">
        {isLoading ? <div className="spinner" /> : (
          <table>
            <thead><tr>
              <th>MRN No.</th><th>PO No.</th><th>Vendor</th><th>Received Date</th><th>Received By</th><th>Amount</th><th>Status</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={7}><div className="empty-state"><ClipboardList /><p>No MRNs found</p></div></td></tr>}
              {rows.map(r => (
                <tr key={r.id}>
                  <td className="mono">{r.mrn_number}</td>
                  <td className="mono">{r.po_number}</td>
                  <td style={{ fontWeight: 500 }}>{r.vendor_name}</td>
                  <td>{fmtDate(r.received_date)}</td>
                  <td>{r.received_by || '—'}</td>
                  <td className="amount">₹{fmtCur(r.total_amount)}</td>
                  <td><span className={`badge badge-${r.status}`}>{r.status}</span></td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr>
              <td colSpan={5} style={{ padding: '10px 18px', textAlign: 'right', fontWeight: 600, background: 'var(--gray-50)' }}>Total</td>
              <td colSpan={2} style={{ padding: '10px 18px', fontWeight: 700, fontFamily: 'var(--mono)', background: 'var(--gray-50)' }}>₹{total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
            </tr></tfoot>
          </table>
        )}
      </div>
    </div>
  );
}

/* ── Main Reports Page ───────────────────────────────────────── */
const TABS = [
  { id: 'stock',     label: 'Stock Report',    Icon: Package },
  { id: 'purchases', label: 'Purchase Report', Icon: ShoppingCart },
  { id: 'issues',    label: 'Issue Report',    Icon: ArrowUpDown },
  { id: 'mrn',       label: 'MRN Report',      Icon: ClipboardList },
];

export default function Reports() {
  const [tab, setTab] = useState('stock');

  return (
    <>
      <div className="topbar">
        <span className="topbar-title">Reports & Analytics</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--gray-400)' }}>
          <FileSpreadsheet size={14} style={{ color: '#217346' }} /> All reports export to Excel (.xlsx)
        </div>
      </div>
      <div className="page-content">
        {/* Tab bar */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: 16 }}>
          {TABS.map(({ id, label, Icon }) => (
            <button key={id} onClick={() => setTab(id)} style={{
              display: 'flex', alignItems: 'center', gap: 7,
              padding: '10px 18px', border: 'none', background: 'transparent',
              fontFamily: 'var(--font)', fontSize: 13, fontWeight: 500, cursor: 'pointer',
              color: tab === id ? 'var(--accent)' : 'var(--gray-400)',
              borderBottom: tab === id ? '2px solid var(--accent)' : '2px solid transparent',
              marginBottom: -1, transition: 'all 0.12s',
            }}>
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>

        <div className="card">
          {tab === 'stock'     && <StockReport />}
          {tab === 'purchases' && <PurchaseReport />}
          {tab === 'issues'    && <IssueReport />}
          {tab === 'mrn'       && <MRNReport />}
        </div>
      </div>
    </>
  );
}